import React, { useState, useEffect } from 'react';
import { useCafe } from '../context/CafeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Coffee
} from 'lucide-react';
import api from '../config/api';
import ImageUpload from '../components/ImageUpload';
import AddMenuItemModal from '../components/AddMenuItemModal';
import NepaliTextInput from '../components/NepaliTextInput';
import { useLanguage } from '../context/LanguageContext';
import { itemLabel } from '../utils/itemName';
import { showNotification } from '../components/GlobalNotification';
import { getItemImageSrc } from '../utils/itemImage';

function Settings() {
  const { items, fetchItems, addItem, updateItem, deleteItem, refreshItems, refreshActiveOrders } = useCafe();
  const { user, setTenantId, getCurrentTenantId } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [tenantUsername, setTenantUsername] = useState(getCurrentTenantId() || '');
  const [tenantOptions, setTenantOptions] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  
  // Form states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form data
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (items.length === 0) {
      fetchItems();
    }
  }, [fetchItems, items.length]);

  // Only a super admin can scope to another tenant, and only they can read this
  // endpoint - everyone else is pinned to their own tenant by the API.
  useEffect(() => {
    if (user?.role !== 'super_admin') return;

    let cancelled = false;
    setTenantsLoading(true);

    api
      .get('/api/tenants')
      .then((response) => {
        if (!cancelled) setTenantOptions(response.data);
      })
      .catch(() => {
        if (!cancelled) showNotification('error', 'Could not load the list of businesses');
      })
      .finally(() => {
        if (!cancelled) setTenantsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const handleSaveTenant = async () => {
    const trimmed = (tenantUsername || '').trim();
    if (!trimmed) {
      showNotification('error', 'Pick a business first, or use Clear Filter to see them all');
      return;
    }

    // Use the new setTenantId method from AuthContext
    const selected = tenantOptions.find((tenant) => tenant.tenantId === trimmed);
    setTenantId(trimmed, selected?.tenantName);
    showNotification('success', `Viewing ${selected?.tenantName || trimmed}`);

    // Refresh data after setting tenant ID
    await refreshItems();
    await refreshActiveOrders();
  };



  // Add new item (handled by modal)
  const handleAddItem = async (itemData) => {
    await addItem(itemData);
  };

  // Start editing item
  const handleEditItem = (item) => {
    setEditingId(item._id);
    setEditData({
      name: item.name,
      nameNepali: item.nameNepali || '',
      price: item.price,
      stock: item.stock || 0,
      includeInMenu: item.includeInMenu !== undefined ? item.includeInMenu : true,
      image: item.image || null,
      isInventoryItem: item.isInventoryItem || false,
      lowStockLimit: item.lowStockLimit || 5
    });
  };

  // Save edited item
  const handleSaveEdit = async (itemId) => {
    if (!editData.name.trim() || !editData.price || editData.price <= 0) {
      showNotification('error', 'Please provide valid item name and price');
      return;
    }

    if (editData.stock !== undefined && (isNaN(editData.stock) || editData.stock < 0)) {
      showNotification('error', 'Please provide a valid stock quantity');
      return;
    }

    try {
      await updateItem(itemId, {
        name: editData.name.trim(),
        nameNepali: (editData.nameNepali || '').trim(),
        price: Number(editData.price),
        stock: parseInt(editData.stock) || 0,
        includeInMenu: editData.includeInMenu,
        image: editData.image,
        isInventoryItem: editData.isInventoryItem,
        lowStockLimit: parseInt(editData.lowStockLimit) || 5
      });
      
      setEditingId(null);
      setEditData({});
      showNotification('success', 'Item updated successfully!');
    } catch (error) {
      showNotification('error', 'Failed to update item');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  // Delete item
  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to delete "${itemName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteItem(itemId);
      showNotification('success', 'Item deleted successfully!');
    } catch (error) {
      showNotification('error', 'Failed to delete item');
    }
  };



  // Fill the menu from the master default list. The server says exactly what
  // it did - how many were added, or why nothing was - so its message is the
  // one shown rather than a generic line that hides the reason.
  const handleInitializeItems = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/items/initialize');
      await fetchItems();
      showNotification('success', response.data?.message || 'Default items added!');
    } catch (error) {
      showNotification(
        'error',
        error.response?.data?.message || 'Failed to initialize default items'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Add Menu Item Modal */}
      <AddMenuItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddItem={handleAddItem}
        loading={loading}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center justify-center sm:justify-start px-6 py-3 text-base font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Item
        </button>
      </div>

      {/* Tenant Settings */}
      {user && user.role === 'super_admin' && (
        <div className="card mb-8 border-l-4 border-l-blue-500">
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">🔧 Tenant Management</h3>
              <p className="text-gray-600 text-sm">Configure tenant-specific data access and management</p>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-blue-900 mb-2">Super Admin Access</h4>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    As a super admin, you can view and manage data for any tenant by setting the tenant ID below. 
                    Leave the field empty to work without tenant restrictions and access global data.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-xl">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tenant <span className="text-gray-500 font-normal">(Optional)</span>
                  </label>
                  {/*
                    A dropdown of tenants that actually exist, not a free-text box.
                    Typed by hand, one wrong character silently scoped the app to a
                    tenant that does not exist - every screen then looked empty
                    with nothing explaining why.
                  */}
                  <select
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                    value={tenantUsername}
                    onChange={(e) => setTenantUsername(e.target.value)}
                    disabled={tenantsLoading}
                  >
                    <option value="">
                      {tenantsLoading ? 'Loading businesses...' : 'All businesses (no filter)'}
                    </option>
                    {tenantOptions.map((tenant) => (
                      <option key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.tenantName} ({tenant.tenantId})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2 flex items-center">
                    <span className="mr-1">💡</span>
                    Create businesses from the Businesses tab. Selecting one scopes every screen
                    to its data.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => {
                      setTenantUsername('');
                      setTenantId('');
                      showNotification('success', 'Tenant filter cleared');
                      refreshItems();
                      refreshActiveOrders();
                    }}
                    className="flex-1 sm:flex-none px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-medium transition-all duration-200 border border-gray-300 hover:border-gray-400"
                  >
                    Clear Filter
                  </button>
                  <button
                    onClick={handleSaveTenant}
                    className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Apply Tenant Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card border-l-4 border-l-green-500">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Coffee className="h-6 w-6 text-green-600" />
                </div>
              </div>
              
              <button
                onClick={handleInitializeItems}
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Initializing...</span>
                  </>
                ) : (
                  <>
                    <Coffee className="h-5 w-5" />
                    <span>Initialize Default Items</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="card border-l-4 border-l-purple-500">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Menu Overview</h3>
                  <p className="text-sm text-gray-600">Current menu statistics</p>
                </div>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{items.length}</div>
                    <div className="text-xs text-purple-700">Total Items</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {items.filter(item => item.includeInMenu).length}
                    </div>
                    <div className="text-xs text-purple-700">In Menu</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {items.filter(item => item.isInventoryItem).length}
                    </div>
                    <div className="text-xs text-purple-700">Tracked</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {items.filter(item => item.isActive).length}
                    </div>
                    <div className="text-xs text-purple-700">Active</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Items Management */}
      <div className="card border-l-4 border-l-orange-500">
        <div className="space-y-6">
          <div className="border-b border-gray-200 pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">📋 Menu Items Management</h3>
                <p className="text-gray-600 text-sm">View, edit, and manage your cafe menu items</p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full font-medium">
                  {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
              </div>
            </div>
          </div>
        
          {items.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <Coffee className="h-20 w-20 text-gray-400 mx-auto mb-6" />
              <h4 className="text-xl font-medium text-gray-900 mb-6">No Menu Items Yet</h4>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add First Item</span>
                </button>
                <button
                  onClick={handleInitializeItems}
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      <span>Initializing...</span>
                    </>
                  ) : (
                    <>
                      <Coffee className="h-5 w-5" />
                      <span>Initialize Defaults</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {items.sort((a, b) => (b.soldToday || 0) - (a.soldToday || 0)).map((item) => (
                <div key={item._id} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-gray-300 hover:shadow-md transition-all duration-200">
                  {editingId === item._id ? (
                    <div className="space-y-6">
                      {/* Basic Information Section */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          ✏️ Edit Item Details
                        </h4>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Item Name (English) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={editData.name}
                              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                              className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              placeholder="Enter item name"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              वस्तुको नाम (नेपाली){' '}
                              <span className="font-normal text-gray-400">— optional</span>
                            </label>
                            <NepaliTextInput
                              defaultEnabled
                              value={editData.nameNepali || ''}
                              onChange={(nameNepali) => setEditData({ ...editData, nameNepali })}
                              className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              placeholder="नेपाली नाम"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Price (Rs.) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-base">Rs</span>
                              <input
                                type="number"
                                value={editData.price}
                                onChange={(e) => setEditData({ ...editData, price: e.target.value })}
                                className="w-full pl-8 pr-3 py-2.5 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Current Stock
                            </label>
                            <input
                              type="number"
                              value={editData.stock}
                              onChange={(e) => setEditData({ ...editData, stock: e.target.value })}
                              className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              placeholder="0"
                              min="0"
                            />
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={`edit-menu-${item._id}`}
                              checked={editData.includeInMenu}
                              onChange={(e) => setEditData({ ...editData, includeInMenu: e.target.checked })}
                              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`edit-menu-${item._id}`} className="ml-3 text-sm font-medium text-gray-900">
                              📋 Include in Menu
                            </label>
                          </div>
                        </div>
                      </div>
                    
                      {/* Inventory Management for Edit */}
                      <div className="space-y-4 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                        <h5 className="text-base font-semibold text-blue-900 flex items-center">
                          📦 Inventory Management
                        </h5>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`edit-inventory-${item._id}`}
                            checked={editData.isInventoryItem}
                            onChange={(e) => setEditData({ ...editData, isInventoryItem: e.target.checked })}
                            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`edit-inventory-${item._id}`} className="ml-3 text-sm font-medium text-gray-900">
                            Enable inventory tracking for this item
                          </label>
                        </div>
                        
                        {editData.isInventoryItem && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Low Stock Alert Threshold
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={editData.lowStockLimit}
                              onChange={(e) => setEditData({ ...editData, lowStockLimit: e.target.value })}
                              className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              placeholder="5"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              You'll be notified when stock falls below this level
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Image Upload for Edit */}
                      <div className="space-y-4">
                        <h5 className="text-base font-semibold text-gray-900 flex items-center border-b border-gray-200 pb-2">
                          🖼️ Item Image
                        </h5>
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <ImageUpload
                            currentImage={editData.image}
                            onImageChange={(imageUrl) => setEditData({ ...editData, image: imageUrl })}
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Upload a high-quality image to make your menu item more appealing
                          </p>
                        </div>
                      </div>
                      
                      {/* Action Buttons for Edit */}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleSaveEdit(item._id)}
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                          <Save className="h-5 w-5" />
                          <span>Save Changes</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-medium transition-all duration-200 border border-gray-300 hover:border-gray-400"
                        >
                          <X className="h-5 w-5 inline mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Item Header */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          {item.image && (
                            <img
                              src={getItemImageSrc(item.image)}
                              alt={itemLabel(item, language)}
                              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{itemLabel(item, language)}</h3>
                            <div className="flex items-center space-x-4">
                              <span className="text-2xl font-bold text-green-600">Rs {item.price}</span>
                              {item.includeInMenu && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  📋 In Menu
                                </span>
                              )}
                              {item.isInventoryItem && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  📦 Tracked
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditItem(item)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                          >
                            <Edit2 className="h-4 w-4" />
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item._id, itemLabel(item, language))}
                            className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Item Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <div className="text-sm text-gray-500 mb-1">Current Stock</div>
                          <div className="text-2xl font-bold text-gray-900">{item.stock || 0}</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <div className="text-sm text-gray-500 mb-1">Active Orders</div>
                          <div className="text-2xl font-bold text-gray-900">{item.activeOrders || 0}</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <div className="text-sm text-gray-500 mb-1">Menu Status</div>
                          <div className={`text-lg font-semibold ${item.includeInMenu ? 'text-green-600' : 'text-red-600'}`}>
                            {item.includeInMenu ? '✅ Available' : '❌ Hidden'}
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                          <div className="text-sm text-gray-500 mb-1">Inventory Status</div>
                          <div className={`text-lg font-semibold ${
                            item.isInventoryItem && item.stock <= item.lowStockLimit 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }`}>
                            {item.isInventoryItem && item.stock <= item.lowStockLimit ? '⚠️ Low Stock' : '✅ Good'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
           )}
         </div>
       </div>
     </div>
   );
}

export default Settings;